/**
 * Labels, colors, icons, and display constants for the calendar.
 */
import type { ElementType } from 'react';
import type { View } from 'react-big-calendar';
import { CheckCircle2, Clock4, PlayCircle, XCircle, type LucideIcon } from 'lucide-react';
import type { CalendarEventType } from '@/types/calendar.types';

export {
  SESSION_REMOTE_TYPE_CONFIG,
  SESSION_TYPE_CONFIG,
  type SessionTypeConfig,
} from '@/constants/shared-catalog';

/** Status label when an in-person information meeting is completed. */
export const IN_PERSON_INFO_MEETING_COMPLETED_LABEL = 'Réalisée';

/** Empty vacation-zone select: national holidays plus every zone. */
export const CALENDAR_ALL_ZONES_LABEL = 'Toutes les zones';

/** Event kinds shown until the user toggles a chip off. */
export const DEFAULT_CALENDAR_EVENT_TYPES: CalendarEventType[] = [
  'session',
  'event',
  'postit',
  'holiday',
];

/** Sort weight: session, then field event, post-it, holiday. */
export const CALENDAR_EVENT_TYPE_PRIORITY: Record<CalendarEventType, number> = {
  session: 1,
  event: 2,
  postit: 3,
  holiday: 4,
};

/** Roles that sit in the field-event organizer column. */
export const FIELD_EVENT_ORGANIZER_ROLES = new Set<string>(['tutor', 'volunteer', 'coordinator']);

/** RI types: calendar modal shows animators + tutors (no students). */
export const CALENDAR_INFORMATION_MEETING_TYPES = new Set<string>([
  'information_meeting',
  'information_meeting_in_person',
]);

/** Role kinds persisted as RI / RA / terrain animators. */
export const CALENDAR_ANIMATOR_ROLE_KINDS = new Set<string>(['recruiter', 'coordinator']);

/** Welcome meeting type: calendar modal shows animators + student/parent. */
export const CALENDAR_WELCOME_MEETING_TYPE = 'welcome_meeting';

/** Remote field-event backend type (`workshop` + remote mode). */
export const CALENDAR_FIELD_EVENT_SESSION_TYPE = 'workshop';

/** Shared type chips: sidebar icon source, fallback Lucide name, color, and label. */
export const EVENT_TYPE_CONFIG = {
  session: {
    sidebarMenuId: 'menu-sessions',
    fallbackIconName: 'Calendar',
    label: 'Séance',
    color: 'var(--color-2-nuance-1)',
    bgClass: 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)]',
    textClass: 'text-[var(--color-2-nuance-1)]',
    borderClass: 'border-[var(--color-2-nuance-1)]',
  },
  event: {
    sidebarMenuId: 'menu-events',
    fallbackIconName: 'Ticket',
    label: 'Événement',
    color: 'var(--color-4-nuance-1)',
    bgClass: 'bg-[color-mix(in_srgb,var(--color-4-nuance-1)_15%,transparent)]',
    textClass: 'text-[var(--color-4-nuance-1)]',
    borderClass: 'border-[var(--color-4-nuance-1)]',
  },
  postit: {
    sidebarMenuId: undefined,
    fallbackIconName: 'StickyNote',
    label: 'Post-it',
    color: 'var(--color-1-nuance-1)',
    bgClass: 'bg-[color-mix(in_srgb,var(--color-1-nuance-1)_15%,transparent)]',
    textClass: 'text-[var(--color-1-nuance-1)]',
    borderClass: 'border-[var(--color-1-nuance-1)]',
  },
  holiday: {
    sidebarMenuId: 'menu-holidays',
    fallbackIconName: 'Plane',
    label: 'Vacances',
    color: 'var(--color-3-nuance-1)',
    bgClass: 'bg-[color-mix(in_srgb,var(--color-3-nuance-1)_15%,transparent)]',
    textClass: 'text-[var(--color-3-nuance-1)]',
    borderClass: 'border-[var(--color-3-nuance-1)]',
  },
};

/** First-level session-kind chips. Tutorat / RI follow in-person session tabs. */
export const SESSION_SYNC_GROUP_CONFIG: Record<string, { color: string; bgClass: string }> = {
  tutoring: {
    color: 'var(--color-1-nuance-1)',
    bgClass: 'bg-[color-mix(in_srgb,var(--color-1-nuance-1)_15%,transparent)]',
  },
  'information-meeting': {
    color: 'var(--color-2-nuance-1)',
    bgClass: 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)]',
  },
  'welcome-meeting': {
    color: 'var(--color-4-nuance-1)',
    bgClass: 'bg-[color-mix(in_srgb,var(--color-4-nuance-1)_15%,transparent)]',
  },
  'field-event': {
    color: 'var(--color-6-nuance-1)',
    bgClass: 'bg-[color-mix(in_srgb,var(--color-6-nuance-1)_15%,transparent)]',
  },
};

/** Keys of {@link EVENT_TYPE_CONFIG} (`session` | `event` | `postit` | `holiday`). */
export type CalendarEventTypeKey = keyof typeof EVENT_TYPE_CONFIG;

/** Resolved Lucide icon per calendar event type. */
export type EventTypeIcons = Record<CalendarEventTypeKey, LucideIcon>;

/** One type-visibility chip next to the calendar title. */
export interface CalendarFilterToggle {
  type: CalendarEventType;
  label: string;
}

/** Type chips rendered beside the calendar title. */
export const CALENDAR_FILTER_TOGGLES: CalendarFilterToggle[] = [
  { type: 'session', label: 'les séances' },
  { type: 'event', label: 'les événements' },
  { type: 'postit', label: 'les post-its' },
  { type: 'holiday', label: 'les vacances' },
];

/** One row of the legend under the full calendar. */
export interface CalendarLegendItem {
  type: CalendarEventType;
  itemClass: string;
  label: string;
}

/** Plural legend labels (chips use the singular labels in {@link EVENT_TYPE_CONFIG}). */
export const CALENDAR_LEGEND_ITEMS: CalendarLegendItem[] = [
  { type: 'session', itemClass: 'calendar-legend-session', label: 'Séances' },
  { type: 'event', itemClass: 'calendar-legend-event', label: 'Événements' },
  { type: 'postit', itemClass: 'calendar-legend-postit', label: 'Post-its' },
  { type: 'holiday', itemClass: 'calendar-legend-holiday', label: 'Vacances' },
];

/** Day/week view start (00:00). */
export const CALENDAR_DAY_VIEW_MIN = new Date(1970, 0, 1, 0, 0, 0);

/** Day/week view end (23:59). `max` is the last instant the time grid draws. */
export const CALENDAR_DAY_VIEW_MAX = new Date(1970, 0, 1, 23, 59, 59);

/** Opening scroll of the day/week grid; hours before and after stay reachable. */
export const CALENDAR_DAY_VIEW_SCROLL_TO = new Date(1970, 0, 1, 8, 0, 0);

/** RBC views we persist; anything else falls back to month. */
export const CALENDAR_VIEWS = new Set<View>(['month', 'week', 'day', 'agenda']);

/** French chrome strings for react-big-calendar. */
export const CALENDAR_RBC_MESSAGES = {
  next: 'Suivant',
  previous: 'Précédent',
  today: "Aujourd'hui",
  month: 'Mois',
  week: 'Semaine',
  day: 'Jour',
  agenda: 'Agenda',
  date: 'Date',
  time: 'Heure',
  event: 'Événement',
  noEventsInRange: 'Aucun événement dans cette période',
};

/** Middle toolbar button: jumps back to the current period. */
export const CALENDAR_PERIOD_LABEL: Record<View, string> = {
  month: 'Ce mois-ci',
  week: 'Cette semaine',
  day: "Aujourd'hui",
  agenda: 'Cette période',
  work_week: 'Cette semaine',
};

/**
 * Max events kept visible in one start-hour bucket before a `+K` control.
 * Week stays low (narrow columns); day and agenda allow denser lanes.
 */
export const CALENDAR_CLUSTER_MAX_VISIBLE = {
  day: 6,
  week: 2,
  agenda: 6,
} as const;

/** Status chip shape shared by sessions, field events, and post-its. */
export interface CalendarStatusChip {
  label: string;
  icon: ElementType;
  bgClass: string;
  textClass: string;
}

/** Session status chips in the event modal. */
export const SESSION_STATUS_CONFIG: Record<string, CalendarStatusChip> = {
  PLANNED: {
    label: 'Planifiée',
    icon: Clock4,
    bgClass: 'bg-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)]',
    textClass: 'text-[var(--color-1-nuance-1)]',
  },
  IN_PROGRESS: {
    label: 'En cours',
    icon: PlayCircle,
    bgClass: 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)]',
    textClass: 'text-[var(--color-2-nuance-1)]',
  },
  COMPLETED: {
    label: 'Terminée',
    icon: CheckCircle2,
    bgClass: 'bg-success-light',
    textClass: 'text-success',
  },
  CLOSED: {
    label: 'Clôturée',
    icon: CheckCircle2,
    bgClass: 'bg-muted/50',
    textClass: 'text-muted-foreground',
  },
  CANCELLED: {
    label: 'Annulée',
    icon: XCircle,
    bgClass: 'bg-error-light',
    textClass: 'text-error',
  },
  NOT_COMPLETED: {
    label: 'Non réalisée',
    icon: XCircle,
    bgClass: 'bg-[color-mix(in_srgb,var(--status-warning)_15%,transparent)]',
    textClass: 'text-[rgb(var(--status-warning))]',
  },
  PARTICIPANTS_ABSENT: {
    label: 'Non réalisée',
    icon: XCircle,
    bgClass: 'bg-[color-mix(in_srgb,var(--status-warning)_15%,transparent)]',
    textClass: 'text-[rgb(var(--status-warning))]',
  },
};

/** Post-it status chips in the event modal. */
export const POSTIT_STATUS_CONFIG: Record<string, CalendarStatusChip> = {
  en_cours: {
    label: 'En cours',
    icon: PlayCircle,
    bgClass: 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)]',
    textClass: 'text-[var(--color-2-nuance-1)]',
  },
  depasse: {
    label: 'Dépassé',
    icon: XCircle,
    bgClass: 'bg-error-light',
    textClass: 'text-error',
  },
  done: {
    label: 'Terminé',
    icon: CheckCircle2,
    bgClass: 'bg-success-light',
    textClass: 'text-success',
  },
};

/** Post-it thematic labels keyed by API enum. Role keys are resolved in calendar-utils. */
export const THEMATIC_LABELS: Record<string, string> = {
  seances: 'Séances',
  matching: 'Matching',
  etablissements: 'Établissements',
  administration: 'Administration',
  autres: 'Autres',
};

/** Holiday-type labels keyed by API enum. */
export const HOLIDAY_TYPE_LABELS: Record<string, string> = {
  national: 'Jour férié national',
  regional: 'Vacances scolaires',
  establishment_specific: "Spécifique à l'établissement",
};

/** Field-event status chips in the calendar modal. */
export const CALENDAR_EVENT_STATUS_CONFIG: Record<string, CalendarStatusChip> = {
  PLANNED: {
    label: 'Planifié',
    icon: Clock4,
    bgClass: 'bg-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)]',
    textClass: 'text-[var(--color-1-nuance-1)]',
  },
  IN_PROGRESS: {
    label: 'En cours',
    icon: PlayCircle,
    bgClass: 'bg-[color-mix(in_srgb,var(--color-4-nuance-1)_15%,transparent)]',
    textClass: 'text-[var(--color-4-nuance-1)]',
  },
  COMPLETED: {
    label: 'Terminé',
    icon: CheckCircle2,
    bgClass: 'bg-success-light',
    textClass: 'text-success',
  },
  CLOSED: {
    label: 'Clôturé',
    icon: CheckCircle2,
    bgClass: 'bg-muted/50',
    textClass: 'text-muted-foreground',
  },
  CANCELLED: {
    label: 'Annulé',
    icon: XCircle,
    bgClass: 'bg-error-light',
    textClass: 'text-error',
  },
};

/** RBC block fill/border colors keyed by calendar event type. */
export const EVENT_COLOR_MAP = {
  session: {
    bg: 'var(--color-2-nuance-1)',
    border: 'var(--color-2-nuance-2)',
  },
  event: {
    bg: 'var(--color-4-nuance-1)',
    border: 'var(--color-4-nuance-2)',
  },
  postit: {
    bg: 'var(--color-1-nuance-1)',
    border: 'var(--color-1-nuance-2)',
  },
  holiday: {
    bg: 'var(--color-3-nuance-1)',
    border: 'var(--color-3-nuance-2)',
  },
} as const;
